export interface MediaPipeAssets {
  wasmBasePath?: string
  modelAssetPath?: string
  /** True when both assets are served from this origin. */
  selfHosted: boolean
  /** Human-readable note for the UI when we had to fall back. */
  note: string | null
}

async function reachable(url: string): Promise<boolean> {
  try {
    const res = await fetch(url, { method: 'HEAD', cache: 'no-store' })
    return res.ok
  } catch {
    return false
  }
}

/**
 * Prefer locally-served MediaPipe assets, fall back to the public CDNs.
 *
 * `npm run sync-assets` drops the wasm (and, network permitting, the model)
 * into public/mediapipe. Two same-origin HEAD requests at boot are cheap, and
 * they mean the app works identically on a locked-down network and on a laptop
 * at home without anyone having to flip a config flag.
 */
export async function resolveMediaPipeAssets(): Promise<MediaPipeAssets> {
  const base = import.meta.env.BASE_URL || '/'
  const localWasm = `${base}mediapipe/wasm`.replace(/\/{2,}/g, '/')
  const localModel = `${base}mediapipe/face_landmarker.task`.replace(/\/{2,}/g, '/')

  const [wasmOk, modelOk] = await Promise.all([
    reachable(`${localWasm}/vision_wasm_internal.js`),
    reachable(localModel),
  ])

  const notes: string[] = []
  if (!wasmOk) notes.push('wasm bundle')
  if (!modelOk) notes.push('face model')

  return {
    ...(wasmOk ? { wasmBasePath: localWasm } : {}),
    ...(modelOk ? { modelAssetPath: localModel } : {}),
    selfHosted: wasmOk && modelOk,
    note: notes.length
      ? `Loading the ${notes.join(' and ')} from a public CDN. Run \`npm run sync-assets\` to serve ${notes.length > 1 ? 'them' : 'it'} locally instead.`
      : null,
  }
}
